"use client"

import type { ReactNode } from "react"
import { nativeSafeNavigate } from "@/lib/capacitor/nav"
import { LoadingState } from "./LoadingState"

export type DataTableColumn<T> = {
  key: string
  label: string
  align?: "left" | "right" | "center"
  width?: string
  render?: (row: T) => ReactNode
  className?: string
  cellClassName?: string
  /** Oculta la columna en las tarjetas del teléfono (sigue en la tabla). */
  hideOnMobile?: boolean
}

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[]
  data: T[]
  getRowKey?: (row: T, idx: number) => string | number
  onRowClick?: (row: T) => void
  rowHref?: (row: T) => string | undefined
  emptyState?: ReactNode
  loading?: boolean
  className?: string
  /**
   * Debajo de md: "cards" (default) apila cada fila como tarjeta — la primera
   * columna es el título, las demás van como «etiqueta · valor» y las columnas
   * sin etiqueta (acciones) ocupan el ancho completo. "scroll" deja la tabla
   * con scroll horizontal, para tablas numéricas donde comparar columnas importa.
   */
  mobile?: "cards" | "scroll"
}

export function DataTable<T>({
  columns,
  data,
  getRowKey,
  onRowClick,
  rowHref,
  emptyState,
  loading,
  className = "",
  mobile = "cards",
}: DataTableProps<T>) {
  const shellCls =
    "bg-[var(--redy-surface)] border border-[var(--redy-border)] rounded-[14px] [box-shadow:var(--shadow-card)] overflow-hidden"

  if (loading) {
    return (
      <div className={`${shellCls} p-4 ${className}`}>
        <LoadingState.Skeleton type="rows" count={5} />
      </div>
    )
  }

  if (data.length === 0 && emptyState) {
    return <div className={`${shellCls} ${className}`}>{emptyState}</div>
  }

  // Sin `render`, la celda es el campo crudo de la fila. Las filas son
  // objetos con claves string (T no está acotado), así que leemos por
  // `Record<string, unknown>` y estrechamos el VALOR a ReactNode — no la fila
  // entera a `any`.
  const cellOf = (col: DataTableColumn<T>, row: T): ReactNode => {
    if (col.render) return col.render(row)
    const raw = (row as Record<string, unknown>)[col.key]
    return (raw ?? "—") as ReactNode
  }

  const clickHandlerOf = (row: T) => {
    const href = rowHref ? rowHref(row) : undefined
    if (!href && !onRowClick) return undefined
    return () => {
      if (href) {
        // Ver el comentario de nativeSafeNavigate en la fila de la tabla.
        nativeSafeNavigate(href)
      } else if (onRowClick) {
        onRowClick(row)
      }
    }
  }

  const cards = mobile === "cards"
  const [titleCol, ...restCols] = columns.filter((c) => !c.hideOnMobile)

  return (
    <div className={`${shellCls} ${className}`}>
      {cards && titleCol && (
        // Teléfono: en una tabla de 6-8 columnas en 327px las acciones quedaban
        // fuera de pantalla y había que scrollear de lado para llegar a ellas.
        <ul className="md:hidden divide-y divide-[var(--redy-border-l)]">
          {data.map((row, idx) => {
            const onClick = clickHandlerOf(row)
            return (
              <li
                key={getRowKey ? getRowKey(row, idx) : idx}
                onClick={onClick}
                className={[
                  "px-4 py-3.5 space-y-2",
                  onClick ? "cursor-pointer active:bg-[var(--redy-warm-white)]" : "",
                ].join(" ")}
              >
                <div className="text-[14px] font-semibold text-[var(--redy-charcoal)] min-w-0 break-words">
                  {cellOf(titleCol, row)}
                </div>
                {restCols.map((col) =>
                  col.label ? (
                    <div key={col.key} className="flex items-start justify-between gap-3 text-[13px]">
                      <span className="shrink-0 pt-px text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--redy-muted)]">
                        {col.label}
                      </span>
                      <div className="min-w-0 text-right text-[var(--redy-charcoal)] tabular-nums break-words">
                        {cellOf(col, row)}
                      </div>
                    </div>
                  ) : (
                    <div key={col.key} className="pt-1 text-[13px] [&>*]:flex-wrap">
                      {cellOf(col, row)}
                    </div>
                  ),
                )}
              </li>
            )
          })}
        </ul>
      )}
      <div className={cards ? "hidden md:block overflow-x-auto" : "overflow-x-auto"}>
        <table className="w-full">
          <thead>
            <tr className="bg-[var(--redy-warm-white)]">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={[
                    "text-xs font-semibold uppercase tracking-[0.04em] text-[var(--redy-muted)] px-4 py-3",
                    col.align === "right"
                      ? "text-right"
                      : col.align === "center"
                        ? "text-center"
                        : "text-left",
                    col.className ?? "",
                  ].join(" ")}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => {
              const key = getRowKey ? getRowKey(row, idx) : idx
              const href = rowHref ? rowHref(row) : undefined
              const clickable = !!onRowClick || !!href
              const rowCls = [
                "border-t border-[var(--redy-border-l)] transition-colors",
                clickable ? "cursor-pointer hover:bg-[var(--redy-warm-white)]" : "",
              ].join(" ")

              const handleClick = () => {
                if (href) {
                  // DataTable vive en superficies de operador: un location.href
                  // directo a /{slug}/... lo entrega Capacitor a Safari y saca
                  // al operador de la app (App Review 2026-07-27, guideline
                  // 4.0). En iOS el salto pasa por /tablet?to=; un href externo
                  // sigue abriendo fuera del shell, que es lo que toca.
                  nativeSafeNavigate(href)
                } else if (onRowClick) {
                  onRowClick(row)
                }
              }

              return (
                <tr
                  key={key}
                  onClick={clickable ? handleClick : undefined}
                  className={rowCls}
                >
                  {columns.map((col) => {
                    const cellValue = cellOf(col, row)
                    return (
                      <td
                        key={col.key}
                        className={[
                          "text-[13px] text-[var(--redy-charcoal)] px-4 py-3.5",
                          col.align === "right"
                            ? "text-right tabular-nums"
                            : col.align === "center"
                              ? "text-center"
                              : "text-left",
                          col.cellClassName ?? "",
                        ].join(" ")}
                      >
                        {cellValue}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
